import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, getLanguage, flagUrl } from '../i18n/languages';

/** Real flag image (Windows/Chrome don't render flag emoji). Rounded rectangle. */
function Flag({ country, size = 20 }) {
  return (
    <img
      src={flagUrl(country, 40)}
      srcSet={`${flagUrl(country, 80)} 2x`}
      alt=""
      width={size}
      height={Math.round(size * 0.72)}
      loading="lazy"
      style={{
        display: 'block', width: size, height: Math.round(size * 0.72),
        objectFit: 'cover', borderRadius: 3, flexShrink: 0,
        boxShadow: '0 0 0 1px rgba(255,255,255,0.08)',
      }}
    />
  );
}

/**
 * Language switcher dropdown with flags. Persists the choice via i18next's
 * localStorage detector ('fluxbot_lang'). Drop in anywhere — admin header,
 * public header, etc. `compact` shows only the flag (good for tight headers).
 */
export default function LanguageSwitcher({ compact = false, align = 'right', direction = 'auto' }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(direction === 'up');
  const ref = useRef(null);

  // Use i18n.language (the user's actual selection), NOT resolvedLanguage —
  // resolvedLanguage falls back to English whenever the selected language's bundle
  // is empty (translations not filled in yet), which would make the picker appear
  // to "reset to English" even though the choice was saved.
  const current = getLanguage(i18n.language);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  // Decide whether the menu opens up or down. 'auto' flips to upward when there
  // isn't enough room below the button (e.g. the switcher at the sidebar bottom).
  const toggleOpen = () => {
    if (!open) {
      if (direction === 'up') setDropUp(true);
      else if (direction === 'down') setDropUp(false);
      else {
        const rect = ref.current?.getBoundingClientRect();
        const spaceBelow = rect ? window.innerHeight - rect.bottom : 9999;
        setDropUp(spaceBelow < 360); // menu is ~ this tall; flip up if it won't fit
      }
    }
    setOpen(o => !o);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={toggleOpen}
        title={t('language.switch', 'Switch language')}
        aria-label={t('language.switch', 'Switch language')}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          background: 'transparent', border: '1px solid var(--border-subtle, rgba(255,255,255,0.1))',
          borderRadius: 'var(--radius-md, 8px)', padding: compact ? '5px 8px' : '5px 10px',
          color: 'var(--text-secondary, #8b8b9e)', cursor: 'pointer', fontSize: '0.8rem',
          fontWeight: 500, fontFamily: 'var(--font-sans)', lineHeight: 1, transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-hover, rgba(255,255,255,0.2))'; e.currentTarget.style.color = 'var(--text-primary, #e8e8ed)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle, rgba(255,255,255,0.1))'; e.currentTarget.style.color = 'var(--text-secondary, #8b8b9e)'; }}
      >
        <Flag country={current.country} size={20} />
        {!compact && <span>{current.nativeLabel}</span>}
        <span style={{ fontSize: '0.6rem', opacity: 0.7 }}>{dropUp ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            [dropUp ? 'bottom' : 'top']: 'calc(100% + 6px)',
            [align === 'left' ? 'left' : 'right']: 0,
            zIndex: 3000, minWidth: 190, maxHeight: '60vh', overflowY: 'auto',
            background: 'var(--bg-card, #111118)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.1))',
            borderRadius: 'var(--radius-md, 8px)', boxShadow: '0 12px 40px rgba(0,0,0,0.5)', padding: '4px',
          }}
        >
          {LANGUAGES.map(lang => {
            const active = lang.code === current.code;
            return (
              <button
                key={lang.code}
                role="option"
                aria-selected={active}
                onClick={() => choose(lang.code)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%',
                  padding: '8px 10px', background: active ? 'rgba(255,255,255,0.06)' : 'transparent',
                  border: 'none', borderRadius: 'var(--radius-sm, 6px)', cursor: 'pointer',
                  color: active ? 'var(--text-primary, #e8e8ed)' : 'var(--text-secondary, #8b8b9e)',
                  fontSize: '0.82rem', fontWeight: active ? 600 : 500, fontFamily: 'var(--font-sans)',
                  textAlign: 'left', transition: 'background 0.12s',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = active ? 'rgba(255,255,255,0.06)' : 'transparent'; }}
              >
                <Flag country={lang.country} size={22} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lang.nativeLabel}</span>
                  <span style={{ display: 'block', fontSize: '0.66rem', color: 'var(--text-muted, #4a4a5a)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lang.label}</span>
                </span>
                {active && <span style={{ color: 'var(--accent-green, #22c55e)', fontSize: '0.8rem', flexShrink: 0 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
